/**
 * Follow-up park of `propose_custom_agent_config_update`. The tool returns
 * `{ pending: true }` immediately; cards are hydrated from pending
 * `agent_writes` on the parent run. Apply/reject reuse the agent-writes
 * endpoints (`graphAffecting: false`) and invalidate the custom-agent
 * config cache on success.
 */
import { agentWritesApi } from '../../api/agents/agent-writes'
import { customAgentsApi } from '../../api/agents/custom-agents'
import type { InFlightAgentActionProposal } from '../../slices/agent-orchestrator-slice'
import { scheduleHydratePendingAgentWrites } from '../cypher-write-pending'
import {
  parseSettledProposeOutput,
  readStructuredMutationError,
  registerAgentActionLifecycle,
  type AgentActionApplyOutcome,
  type AgentActionCompleteHydration,
  type AgentActionLifecycleCapability,
  type AgentActionRejectOutcome
} from './lifecycle-registry'

const TOOL_NAME = 'propose_custom_agent_config_update'

function readString(record: Record<string, unknown>, key: string): string | null {
  const v = record[key]
  return typeof v === 'string' ? v : null
}

function readProposeOutput(
  parentToolUseId: string,
  output: unknown
): AgentActionCompleteHydration | null {
  if (!output || typeof output !== 'object' || Array.isArray(output)) return null
  const obj = output as Record<string, unknown>
  if (obj.pending !== true) return null
  const planId = readString(obj, 'plan_id')
  if (planId === null) return null
  const writeIds = Array.isArray(obj.write_ids)
    ? obj.write_ids.filter((id): id is string => typeof id === 'string')
    : []
  return {
    parentToolUseId,
    planId,
    results: writeIds.map((writeId) => ({ toolUseId: parentToolUseId, writeId }))
  }
}

export const customAgentConfigUpdateLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'update_custom_agent_config',
  toolName: TOOL_NAME,

  parseToolStart(toolUseId, input, startedAtMs) {
    if (!input || typeof input !== 'object' || Array.isArray(input)) return []
    const obj = input as Record<string, unknown>
    const summary = readString(obj, 'summary')
    if (summary === null || !summary.trim()) return []
    return [
      {
        id: toolUseId,
        proposal: {
          kind: 'update_custom_agent_config',
          configId: '',
          summary: summary.trim(),
          prompt: typeof obj.prompt === 'string' ? obj.prompt : undefined,
          cron: typeof obj.cron === 'string' ? obj.cron : undefined,
          startedAtMs
        }
      }
    ]
  },

  parseToolComplete(toolUseId, output) {
    return readProposeOutput(toolUseId, output)
  },

  parseSettledToolComplete(output) {
    return parseSettledProposeOutput(output)
  },

  scheduleHydratePending(dispatch, conversationId) {
    scheduleHydratePendingAgentWrites(dispatch, conversationId)
  },

  apply(toolUseId) {
    return async (dispatch): Promise<AgentActionApplyOutcome> => {
      try {
        const result = await dispatch(
          agentWritesApi.endpoints.applyCypherWrite.initiate({
            toolUseId,
            graphAffecting: false
          })
        ).unwrap()
        if (result.ok) {
          const applied = result.result ?? {}
          const configId = typeof applied.config_id === 'string' ? applied.config_id : undefined
          dispatch(
            customAgentsApi.util.invalidateTags([
              { type: 'CustomAgentConfig', id: 'LIST' },
              ...(configId ? [{ type: 'CustomAgentConfig' as const, id: configId }] : [])
            ])
          )
          return { kind: 'success' }
        }
        return { kind: 'apply_failed', error: result.error ?? 'Config update apply failed.' }
      } catch (err) {
        const refused = readStructuredMutationError(err)
        if (refused !== null) {
          return { kind: 'apply_failed', error: refused.error }
        }
        const message = err instanceof Error ? err.message : 'Config update apply failed.'
        return { kind: 'transport_error', error: message }
      }
    }
  },

  reject(toolUseId, reason) {
    return async (dispatch): Promise<AgentActionRejectOutcome> => {
      try {
        const result = await dispatch(
          agentWritesApi.endpoints.rejectCypherWrite.initiate({ toolUseId, reason })
        ).unwrap()
        if (result.ok) {
          return { kind: 'success', settledMeta: { rejectionReason: result.reason ?? reason } }
        }
        return { kind: 'reject_failed', error: result.error ?? 'Reject failed.' }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Reject failed.'
        return { kind: 'transport_error', error: message }
      }
    }
  },

  buildResumeRowLine(proposal: InFlightAgentActionProposal, displayNumber) {
    const summary = proposal.summary.replace(/"/g, '').trim()
    const terminal = proposal.terminalState
    if (terminal?.kind === 'applied') {
      return `${displayNumber}. APPLIED — "${summary}"; this agent's config was updated.`
    }
    if (terminal?.kind === 'apply_failed') {
      return `${displayNumber}. APPLY_FAILED — "${summary}"; error: ${terminal.error}.`
    }
    if (terminal?.kind === 'rejected') {
      return `${displayNumber}. REJECTED — "${summary}". The agent config was not changed.`
    }
    return `${displayNumber}. UNDECIDED — "${summary}".`
  }
}

registerAgentActionLifecycle(customAgentConfigUpdateLifecycleCapability)
